import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import React from 'react'
import { ReactComponent as Info } from '@dataplace.ai/assets/lib/icons/largeIcons/info.svg'
import { AttractivenessFormDataObject, AttractivenessFormFields, AttractivenessFormState, FormElement } from '../../@types/IAttractivenessTileData'
import { useGetAttractivenessDropdownOptions } from './useGetAttractivenessDropdownOptions'
import { COMPONENT_TYPES } from '../../constants'

const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-bottom: 1rem;
`
const DescriptionRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-top: 0.15rem;
`

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.2rem;
  align-items: start;

  > svg {
    path {
      stroke: #234596;
    }
  }
`

export const useAttractivenessFormData = (formState: AttractivenessFormState): AttractivenessFormDataObject => {
  const { t } = useTranslation()

  const iconWH = 24

  const {
    businessTypeOptions,
    marketTypeOptions,
    ageBucketOptions,
    trafficTypeOptions,
    transportMeansOptions,
    daypartOptions,
    weekpartOptions,
  } = useGetAttractivenessDropdownOptions()

  const formElementData: FormElement[] = [
    {
      sectionTitle: t('placeme.attractiveness.section.market_type.title'),
      sectionDescription: (
        <DescriptionWrapper>
          <span>{t('placeme.attractiveness.section.market_type.description_1')}</span>
          <InfoRow>
            <Info
              height={iconWH}
              width={iconWH}
            />
            <DescriptionRow>
              <span>{t('placeme.attractiveness.section.market_type.description_2')}</span>
              <span>{t('placeme.attractiveness.section.market_type.description_3')}</span>
            </DescriptionRow>
          </InfoRow>
        </DescriptionWrapper>
      ),
      formStateElement: formState.market,
      inputKey: AttractivenessFormFields.market,
      options: marketTypeOptions,
      title: t('placeme.attractiveness.section.market_type.title'),
      componentType: COMPONENT_TYPES.DROPDOWN,
    },
    {
      sectionTitle: t('placeme.attractiveness.section.business_type.title'),
      sectionDescription: t('placeme.attractiveness.section.business_type.description'),
      formStateElement: formState.businessType,
      inputKey: AttractivenessFormFields.businessType,
      options: businessTypeOptions,
      title: t('placeme.attractiveness.business_type'),
      componentType: COMPONENT_TYPES.DROPDOWN,
    },
    {
      sectionTitle: t('placeme.attractiveness.section.age.title'),
      sectionDescription: t('placeme.attractiveness.section.age.description'),
      formStateElement: formState.ageBuckets,
      inputKey: AttractivenessFormFields.ageBuckets,
      options: ageBucketOptions,
      title: t('placeme.attractiveness.age_buckets'),
      componentType: COMPONENT_TYPES.MULTI_TILES,
    },
    {
      sectionTitle: t('placeme.attractiveness.section.traffic.title'),
      sectionDescription: t('placeme.attractiveness.section.traffic.description'),
      formStateElement: formState.trafficType,
      inputKey: AttractivenessFormFields.trafficType,
      options: trafficTypeOptions,
      title: t('placeme.attractiveness.traffic_type'),
      componentType: COMPONENT_TYPES.DROPDOWN,
    },
    {
      sectionTitle: t('placeme.attractiveness.section.transport_means.title'),
      sectionDescription: t('placeme.attractiveness.section.transport_means.description'),
      formStateElement: formState.transportMeans,
      inputKey: AttractivenessFormFields.transportMeans,
      options: transportMeansOptions,
      title: t('placeme.attractiveness.transport_means'),
      componentType: COMPONENT_TYPES.DROPDOWN,
    },
    {
      sectionTitle: t('placeme.attractiveness.section.dayparts.title'),
      sectionDescription: t('placeme.attractiveness.section.dayparts.description'),
      formStateElement: formState.dayparts,
      inputKey: AttractivenessFormFields.dayparts,
      options: daypartOptions,
      title: t('placeme.attractiveness.dayparts'),
      componentType: COMPONENT_TYPES.MULTI_TILES,
    },
    {
      formStateElement: formState.weekparts,
      inputKey: AttractivenessFormFields.weekparts,
      options: weekpartOptions,
      title: t('placeme.attractiveness.weekparts'),
      componentType: COMPONENT_TYPES.MULTI_TILES,
    },
  ]

  return {
    formElementData,
  }
}
